export const RETREAT_TYPES = [
  "Established Parish",
  "New Parish / First Retreat",
  "Archbishop Outreach Retreat",
] as const;

export const SEASONS = ["Winter", "Spring", "Summer", "Fall"] as const;

export const FUNDING_STATUSES = [
  "Not reviewed",
  "Discussion planned",
  "Requested — awaiting decision",
  "Confirmed",
  "Not available",
  "Not needed",
] as const;

export const BUDGET_LINE_KEYS = [
  { key: "REGISTRATIONS", label: "Registrations covered" },
  { key: "TRANSPORTATION", label: "Transportation" },
  { key: "CATERING", label: "Catering / meals" },
  { key: "LODGING", label: "Lodging" },
  { key: "MATERIALS", label: "Materials / supplies" },
  { key: "OTHER", label: "Other tentative expense" },
] as const;

export const SUPPORT_TYPES = {
  STEERING_COMMITTEE: "STEERING_COMMITTEE",
  EXECUTIVE_COMMITTEE: "EXECUTIVE_COMMITTEE",
  EXPERIENCED_COUPLE: "EXPERIENCED_COUPLE",
} as const;

export type SupportTypeCode =
  (typeof SUPPORT_TYPES)[keyof typeof SUPPORT_TYPES];

/** Shared display labels for support oversight (form + ManageRetreat). */
export const SUPPORT_TYPE_LABELS: Record<string, string> = {
  STEERING_COMMITTEE: "Steering Committee",
  EXECUTIVE_COMMITTEE: "Executive Committee",
  EXPERIENCED_COUPLE: "Mentor Couple",
};

export const SUPPORT_LABELS: Record<SupportTypeCode, string> = {
  STEERING_COMMITTEE: "Steering Committee",
  EXECUTIVE_COMMITTEE: "Executive Committee",
  EXPERIENCED_COUPLE: "Mentor Couple",
};

export const ELIGIBILITY_ITEMS = [
  {
    id: "MARRIAGE_FAITH_ELIGIBILITY",
    label: "Catholic couple, sacramentally married in the Catholic Church.",
    helper:
      "Use a note for relevant Church marriage-process context or required follow-up.",
  },
  {
    id: "MARRIAGE_DURATION_REQUIREMENT",
    label:
      "Sacramentally married in the Catholic Church for at least 5 years.",
    helper:
      "If an exception is requested or approved, document the circumstances and decision authority.",
  },
  {
    id: "PARISH_GOOD_STANDING",
    label:
      "Parishioners in good standing with the host parish, or approved under the LOVESTRONG first-retreat exception.",
    helper:
      "For the LOVESTRONG first-retreat exception, document the approval and basis.",
  },
  {
    id: "LEAD_EXPERIENCE_TRAINING",
    label: "Lead Couple Training will be completed before team meetings begin.",
    helper:
      "When training is scheduled rather than complete, document the planned date or follow-up.",
  },
] as const;

export type Season = (typeof SEASONS)[number];

export type ParishRow = {
  id: number;
  parish?: string;
  name?: string;
  city?: string;
  state?: string;
  address?: string;
  zip?: string;
  postalCode?: string;
};

export type CoupleRow = {
  id: number;
  coupleId?: number;
  hisFirstName?: string;
  hisLastName?: string;
  hisEmail?: string;
  herFirstName?: string;
  herLastName?: string;
  herEmail?: string;
  notes?: string;
  address?: string;
};

export type PlanningSupportRow = {
  coupleId?: number;
  userId?: number;
  supportType: SupportTypeCode | string;
  addedAt?: string;
  coupleName?: string;
};

export type CoupleOption = { value: number; label: string; couple: CoupleRow };

export type SupportPerson = {
  id: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  parishId?: number;
};

export type OversightContactKind = "user" | "couple";

export type OversightContactOption = {
  value: string;
  label: string;
  kind: OversightContactKind;
  id: number;
};

export const emptyBudget = () =>
  Object.fromEntries(BUDGET_LINE_KEYS.map((l) => [l.key, ""])) as Record<
    string,
    string
  >;

export const emptyEligibility = () =>
  Object.fromEntries(
    ELIGIBILITY_ITEMS.map((i) => [
      i.id,
      { checked: false, note: "", showNote: false },
    ]),
  ) as Record<string, { checked: boolean; note: string; showNote: boolean }>;
